import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { formatMessageText } from '../../../../utils/sanitize';
import { ensureSignatureLineBreak } from '../../../../utils/messageSignature';
import { isInstagramCdnAvatarUrl, isTemporaryRemoteMediaUrl } from '../../../../utils/failedImageCache';
import { ChatMessage } from '../../hooks/types';
import { MediaLightbox } from './media/MediaLightbox';
import { chatMediaIconName } from './media/MediaFallback';

interface QuotedMessagePreviewProps {
    msg: ChatMessage;
    isMe: boolean;
    conversationName: string;
    onScrollToMessage?: (messageId: string) => void;
}

function canShowQuotedMedia(url?: string): boolean {
    if (!url) return false;
    return !isTemporaryRemoteMediaUrl(url) || isInstagramCdnAvatarUrl(url);
}

/**
 * Citação no balão — layout tipo WhatsApp (Jakob):
 * barra à esquerda, nome + trecho, thumbnail à direita (sem overlay).
 */
export const QuotedMessagePreview: React.FC<QuotedMessagePreviewProps> = ({
    msg,
    isMe,
    conversationName,
    onScrollToMessage,
}) => {
    const [thumbFailed, setThumbFailed] = React.useState(false);
    const [lightboxOpen, setLightboxOpen] = React.useState(false);

    React.useEffect(() => {
        setThumbFailed(false);
    }, [msg.quotedMessage?.mediaUrl]);

    if (!msg.quotedMessage) return null;

    const quote = msg.quotedMessage;
    // Reply de Story usa layout Instagram fora do balão (InstagramStoryReply).
    if (quote.id.startsWith('ig-story:')) return null;

    const inferredVisualType: 'image' | 'video' | null = (() => {
        if (quote.type === 'image' || quote.type === 'video') return quote.type;
        if (!quote.mediaUrl) return null;
        if (quote.type === 'text') {
            return /\.(mp4|mov|m4v|webm)(\?|$)/i.test(quote.mediaUrl) ? 'video' : 'image';
        }
        return null;
    })();
    const isVisualQuote = inferredVisualType !== null;
    const showQuotedThumb =
        !!quote.mediaUrl &&
        isVisualQuote &&
        !thumbFailed &&
        canShowQuotedMedia(quote.mediaUrl);
    const canOpenLightbox =
        !!quote.mediaUrl && isVisualQuote && canShowQuotedMedia(quote.mediaUrl) && !thumbFailed;
    const visualType = inferredVisualType || 'image';
    const mediaIcon = chatMediaIconName(quote.type);
    const senderLabel = quote.sender === 'me' ? 'Você' : conversationName;

    const handleQuoteClick = () => {
        if (canOpenLightbox) {
            setLightboxOpen(true);
            return;
        }
        onScrollToMessage?.(quote.id);
    };

    return (
        <>
            <blockquote
                cite={`#msg-${quote.id}`}
                className={[
                    'mx-1.5 mt-1.5 mb-0.5 flex min-w-0 items-stretch overflow-hidden rounded-md border-l-[3px]',
                    'cursor-pointer transition-colors motion-safe:duration-150',
                    isMe
                        ? 'border-chat-bubble-me-fg/80 bg-black/20 hover:bg-black/25'
                        : 'border-primary bg-foreground/5 hover:bg-foreground/8',
                ].join(' ')}
            >
                <button
                    type="button"
                    onClick={handleQuoteClick}
                    className="flex min-h-11 min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left pointer-fine:min-h-9"
                    aria-label={
                        canOpenLightbox
                            ? `Abrir mídia citada de ${senderLabel}`
                            : `Ir para mensagem citada de ${senderLabel}`
                    }
                >
                    <div className="min-w-0 flex-1">
                        <cite
                            className={[
                                'block truncate text-xs font-semibold not-italic',
                                isMe ? 'text-chat-bubble-me-fg' : 'text-primary',
                            ].join(' ')}
                        >
                            {senderLabel}
                        </cite>
                        <p
                            className={[
                                'mt-0.5 flex min-w-0 items-start gap-1 text-xs leading-snug',
                                isMe ? 'text-chat-bubble-me-fg/80' : 'text-muted-foreground',
                            ].join(' ')}
                        >
                            {mediaIcon ? (
                                <AppIcon
                                    name={mediaIcon}
                                    className="mt-0.5 size-3.5 shrink-0 opacity-80"
                                    aria-hidden="true"
                                />
                            ) : null}
                            <span
                                className="min-w-0 whitespace-pre-line font-sans line-clamp-2"
                                dangerouslySetInnerHTML={{
                                    __html: formatMessageText(
                                        ensureSignatureLineBreak(quote.content || 'Mensagem'),
                                        isMe,
                                    ),
                                }}
                            />
                        </p>
                    </div>

                    {showQuotedThumb ? (
                        <span className="relative size-11 shrink-0 overflow-hidden rounded-sm bg-foreground/10 pointer-fine:size-10">
                            {visualType === 'image' ? (
                                <img
                                    src={quote.mediaUrl}
                                    alt=""
                                    className="size-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                    referrerPolicy="no-referrer"
                                    onError={() => setThumbFailed(true)}
                                />
                            ) : (
                                <video
                                    src={quote.mediaUrl}
                                    className="size-full object-cover"
                                    muted
                                    playsInline
                                    preload="metadata"
                                    onError={() => setThumbFailed(true)}
                                />
                            )}
                            {visualType === 'video' ? (
                                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25">
                                    <AppIcon name="video" className="size-3.5 text-primary-foreground drop-shadow" aria-hidden="true" />
                                </span>
                            ) : null}
                        </span>
                    ) : null}
                </button>
            </blockquote>

            {lightboxOpen && quote.mediaUrl ? (
                <MediaLightbox
                    items={[{
                        url: quote.mediaUrl,
                        type: visualType === 'video' ? 'video' : 'image',
                        caption: quote.content,
                    }]}
                    initialIndex={0}
                    onClose={() => setLightboxOpen(false)}
                />
            ) : null}
        </>
    );
};
